// Client account state — optional username/password sign-in.
//
// The session is a bearer token in localStorage with a 1-year server expiry,
// so users stay signed in until they explicitly sign out. While signed in,
// watch progress and My List mirror to the server (see storage/progress.js +
// storage/watchlist.js) so they follow the user across devices.
//
// Signed-out behavior is unchanged: everything works from localStorage alone.
import { useSyncExternalStore } from "react";

const KEY = "poku.auth";

let cached = null;
let cachedRaw = null;
const listeners = new Set();

function read() {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw === cachedRaw) return cached;
    cachedRaw = raw;
    cached = raw ? JSON.parse(raw) : null; // { username, token }
  } catch {
    cached = null;
  }
  return cached;
}

function write(value) {
  cached = value;
  try {
    if (value) localStorage.setItem(KEY, JSON.stringify(value));
    else localStorage.removeItem(KEY);
    cachedRaw = localStorage.getItem(KEY);
  } catch {
    /* storage unavailable — session lives in memory only */
  }
  listeners.forEach((fn) => fn());
}

export function getAccount() {
  return read();
}

export function setAccount(username, token) {
  write({ username, token });
}

export function clearAccount() {
  write(null);
}

export function useAccount() {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    read,
  );
}

// Fetch with the bearer token when signed in. 401 clears the stale session
// (e.g. server data reset) so the UI falls back to signed-out state.
export async function authFetch(url, opts = {}, token = null) {
  const t = token || read()?.token;
  const headers = { ...(opts.headers || {}) };
  if (t) headers["authorization"] = `Bearer ${t}`;
  const res = await fetch(url, { ...opts, headers });
  if (res.status === 401 && t) clearAccount();
  return res;
}

// Push local progress + watchlist; adopt the server's merged result.
// Called after sign-in (full merge) and by the storage mirrors (debounced).
export async function syncNow(merge = true) {
  const acc = read();
  if (!acc?.token) return null;
  const { getAllProgress } = await import("../storage/progress.js");
  const { getAllWatchlist } = await import("../storage/watchlist.js");
  const body = JSON.stringify({ progress: getAllProgress(), watchlist: getAllWatchlist() });
  const res = await authFetch("/api/auth/sync", { method: "POST", headers: { "content-type": "application/json" }, body }, acc.token);
  if (!res.ok) return null;
  const merged = await res.json();
  if (merge) {
    const progress = await import("../storage/progress.js");
    const watchlist = await import("../storage/watchlist.js");
    progress.mergeFromServer(merged.progress || []);
    watchlist.mergeFromServer(merged.watchlist || []);
  }
  return merged;
}

// Sign in (or sign up) and reconcile local + server data: push everything
// local, then adopt the merged result so both sides end up identical.
export async function signIn(username, password, mode = "login") {
  const res = await fetch(`/api/auth/${mode === "signup" ? "signup" : "login"}`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ username, password }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) return { error: data.error || "Could not sign in. Try again." };
  await adoptSession(data);
  return { user: data.user };
}

// Google sign-in (web only): exchange the GIS ID token for a PoKu session,
// then reconcile exactly like password sign-in. Shared helper for both paths.
export async function signInWithGoogle(credential) {
  const res = await fetch("/api/auth/google", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ credential }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) return { error: data.error || "Google sign-in failed. Try again." };
  await adoptSession(data);
  return { user: data.user };
}

async function adoptSession(data) {
  setAccount(data.user.username, data.token);
  try {
    await syncNow(true);
  } catch {
    /* sync is best-effort; the session itself succeeded */
  }
}

// The public GIS client id (server owns the value; 501 => button hidden).
export async function googleClientId() {
  try {
    const res = await fetch("/api/auth/google");
    if (!res.ok) return null;
    const data = await res.json();
    return data.clientId || null;
  } catch {
    return null;
  }
}

export async function signOut() {
  clearAccount();
}
